import ModernVoiceConfig from './ModernVoiceConfig';

const personalities = [
  { name: 'Professional', description: 'Formal, reliable, and straightforward communication style focused on efficiency.', icon: '👔' },
  { name: 'Friendly', description: 'Warm, approachable, and conversational tone that builds rapport quickly.', icon: '😊' },
  { name: 'Casual', description: 'Relaxed, easy-going style with a touch of humor and modern language.', icon: '😎' },
  { name: 'Technical', description: 'Precise, detailed, and information-rich responses optimized for technical topics.', icon: '🖥️' },
  { name: 'Empathetic', description: 'Understanding, compassionate, and supportive tone focused on customer needs.', icon: '🤗' },
  { name: 'Custom', description: 'Create your own unique personality by defining specific traits and characteristics.', icon: '✏️' },
];

const PersonalityConfig = ({ personality, setPersonality, onSave, onBack }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    setPersonality(prev => ({ ...prev, [name]: value }));
  };

  const handlePersonalitySelect = (personalityType) => {
    setPersonality(prev => ({ ...prev, personalityType }));
  };

  return (
    <div className="w-full max-w-5xl mx-auto flex flex-col">
      <h1 className="text-3xl font-extrabold text-gray-900 mb-4 text-center tracking-tight">AI Agent Personality</h1>
      {/* Name & Greeting side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
        <div>
          <label className="block text-base font-semibold text-gray-700 mb-1">Agent Name</label>
          <input
            type="text"
            name="name"
            value={personality.name || ''}
            onChange={handleChange}
            className="w-full max-w-md p-2 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-base shadow-sm transition"
            placeholder="e.g. Professional Helper"
          />
        </div>
        <div>
          <label className="block text-base font-semibold text-gray-700 mb-1">Greeting Message</label>
          <input
            type="text"
            name="greeting"
            value={personality.greeting || ''}
            onChange={handleChange}
            className="w-full max-w-md p-2 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-base shadow-sm transition"
            placeholder="Hello! I'm your professional assistant. How may I help you?"
          />
        </div>
      </div>
      {/* Description */}
      <div className="mb-3">
        <label className="block text-base font-semibold text-gray-700 mb-1">Agent Description</label>
        <textarea
          name="description"
          value={personality.description || ''}
          onChange={handleChange}
          className="w-full p-2 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-base shadow-sm transition"
          rows="2"
          placeholder="A formal and helpful assistant that provides clear, concise answers while maintaining a professional tone. Focuses on efficiency and accuracy while being respectful and courteous."
        />
      </div>
      <hr className="my-3 border-gray-200" />
      {/* Personality Selection */}
      <div className="mb-3">
        <label className="block text-base font-semibold text-gray-700 mb-2">Personality</label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {personalities.map((personalityOption) => (
            <div
              key={personalityOption.name}
              className={`p-4 rounded-lg cursor-pointer border flex flex-col items-start transition-all duration-300 bg-white shadow-sm hover:shadow-md hover:scale-[1.02] ${
                personality.personalityType === personalityOption.name
                  ? 'bg-blue-50 border-blue-500 shadow-md scale-105' : 'border-gray-200'
              }`}
              style={{ minWidth: 0 }}
              onClick={() => handlePersonalitySelect(personalityOption.name)}
            >
              <div className="flex items-center mb-2">
                <span className="text-2xl mr-3">{personalityOption.icon}</span>
                <h3 className="text-base font-semibold text-gray-800">{personalityOption.name}</h3>
              </div>
              <p className="text-gray-500 text-sm mt-1">{personalityOption.description}</p>
            </div>
          ))}
        </div>
      </div>
      <hr className="my-3 border-gray-200" />
      {/* Voice Config */}
      <div className="mb-2">
        <h2 className="text-xl font-bold text-gray-800 mb-1">Voice & Speech Settings</h2>
        <p className="text-gray-500 mb-2">Select a voice for your AI agent when communicating through voice channels.</p>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm">
          <ModernVoiceConfig
            value={personality.voice || {}}
            onChange={voice => setPersonality(prev => ({ ...prev, voice }))}
          />
        </div>
      </div>
    </div>
  );
};

export default PersonalityConfig;
